// A counterparty's quotes (TX-US-0040, ADR-0013): the prices it gave us, when, for what and on
// what conditions. A BD or Operations adds one; everyone who reads the counterparty reads them.
// A quote is not edited: its author deletes it and enters it again, and nobody else deletes it.
//
// Newest quote date first, twenty to a page, keyset as the comments are. A quote just added is on
// the first page, so adding one returns there.
//
// The API is the authority on who may write - `canQuote` and the author check only decide what
// is offered.

import { useEffect, useState } from 'react';

import {
  addCounterpartyQuote,
  deleteCounterpartyQuote,
  getReferenceLists,
  pageCounterpartyQuotes,
} from '../../api.js';
import Pager from '../../ui/Pager.jsx';
import usePageTrail from '../../ui/usePageTrail.js';
import {
  FOR_PRODUCT,
  FOR_SERVICE,
  MAX_CONDITIONS_LENGTH,
  MAX_SERVICE_LENGTH,
  QUOTE_BASES,
  emptyQuote,
  localToday,
  missing,
  priceText,
  quoteBody,
  quoteDate,
  quotedForOf,
  subjectOf,
} from './quotes.js';

export default function CounterpartyQuotes({ counterpartyId, categories, canQuote, myEmail }) {
  // Null when the counterparty's categories do not say; the form then offers the choice.
  const derived = quotedForOf(categories);
  const blank = () => emptyQuote(localToday(), derived ?? FOR_SERVICE);
  const [view, setView] = useState({ cursor: null, reads: 0 });
  const [page, setPage] = useState(null);
  const [lists, setLists] = useState({ product: [], currency: [] });
  const [draft, setDraft] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const trail = usePageTrail({
    query: view,
    page,
    apply: setView,
    withCursor: (current, cursor) => ({ ...current, cursor }),
  });

  useEffect(() => {
    getReferenceLists()
      .then((found) => setLists({ product: found.product ?? [], currency: found.currency ?? [] }))
      .catch((err) => setError(err.message));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    pageCounterpartyQuotes(counterpartyId, view.cursor)
      .then((found) => {
        if (!cancelled) setPage(found);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [counterpartyId, view]);

  const today = localToday();
  const needed = missing(draft, today);
  const set = (field) => (event) => setDraft((current) => ({ ...current, [field]: event.target.value }));
  const productLabel = (code) => lists.product.find((p) => p.code === code)?.label ?? code;

  const reread = () => {
    trail.reset();
    setView((current) => ({ cursor: null, reads: current.reads + 1 }));
  };

  const add = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await addCounterpartyQuote(counterpartyId, quoteBody(draft));
      setDraft(blank());
      reread();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const remove = async (quote) => {
    setBusy(true);
    setError(null);
    try {
      await deleteCounterpartyQuote(counterpartyId, quote.quote_id);
      reread();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const quotes = page?.quotes ?? [];
  const paged = trail.hasPrevious || Boolean(page?.has_more);

  return (
    <section className="counterparty-quotes" aria-labelledby="quotes-heading">
      <h3 id="quotes-heading">Quotes</h3>

      {error && <p className="error">{error}</p>}

      {canQuote && (
        <form className="quote-form" onSubmit={add} aria-label="Add a quote">
          <label>
            <span>Quoted on</span>
            <input type="date" value={draft.quotedOn} max={today} onChange={set('quotedOn')} />
          </label>

          {derived === null && (
            <fieldset className="quote-for">
              <legend>Quoted for</legend>
              {[[FOR_SERVICE, 'A service'], [FOR_PRODUCT, 'A product']].map(([value, label]) => (
                <label key={value} className="choice">
                  <input type="radio" name="quoted-for" value={value}
                         checked={draft.quotedFor === value} onChange={set('quotedFor')} />
                  {label}
                </label>
              ))}
            </fieldset>
          )}

          {draft.quotedFor === FOR_PRODUCT ? (
            <label>
              <span>Product</span>
              <select value={draft.product} onChange={set('product')}>
                <option value="">Choose a product</option>
                {lists.product.map((p) => <option key={p.code} value={p.code}>{p.label}</option>)}
              </select>
            </label>
          ) : (
            <label>
              <span>Service</span>
              <input type="text" value={draft.service} onChange={set('service')}
                     maxLength={MAX_SERVICE_LENGTH} placeholder="Transport Ndola → Kolwezi" />
            </label>
          )}

          <div className="quote-price">
            <label>
              <span>Price</span>
              <input type="text" inputMode="decimal" value={draft.amount} onChange={set('amount')}
                     placeholder="38.00" />
            </label>
            <label>
              <span>Currency</span>
              <select value={draft.currency} onChange={set('currency')}>
                {(lists.currency.length ? lists.currency : [{ code: 'USD', label: 'USD' }])
                  .map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
              </select>
            </label>
            <label>
              <span>Basis</span>
              <select value={draft.per} onChange={set('per')}>
                {QUOTE_BASES.map((b) => <option key={b.code} value={b.code}>{b.label}</option>)}
              </select>
            </label>
          </div>

          <label className="quote-conditions">
            <span>Conditions</span>
            <textarea rows={3} value={draft.conditions} onChange={set('conditions')}
                      maxLength={MAX_CONDITIONS_LENGTH}
                      placeholder="Incoterm and place, payment terms, validity, minimum volume, what is included" />
          </label>

          {needed.length > 0 && <p className="muted">Still needs {needed.join(', ')}.</p>}
          <button type="submit" disabled={busy || needed.length > 0}>Add quote</button>
        </form>
      )}

      {page && quotes.length === 0 && <p className="muted">No quotes.</p>}

      {quotes.length > 0 && (
        <ol className="quote-list" aria-label="Quotes">
          {quotes.map((quote) => (
            <li key={quote.quote_id}>
              <header>
                <time dateTime={quote.quoted_on}>{quoteDate(quote.quoted_on)}</time>
                <strong>{subjectOf(quote, productLabel)}</strong>
                <span className="quote-amount">{priceText(quote)}</span>
                <span className="muted" title={quote.created_by}>{quote.author_name}</span>
                {quote.created_by === myEmail && (
                  <button type="button" className="link-button" disabled={busy}
                          onClick={() => remove(quote)}>
                    Delete
                  </button>
                )}
              </header>
              <p>{quote.conditions}</p>
            </li>
          ))}
        </ol>
      )}

      {paged && (
        <Pager busy={busy} hasPrevious={trail.hasPrevious} hasMore={Boolean(page?.has_more)}
               onPrevious={trail.onPrevious} onNext={trail.onNext} />
      )}
    </section>
  );
}
